import { useEffect, useState } from 'react';
import { Plus, Trash, PencilSimple, DotsSixVertical } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import { useAuth } from '../../contexts/AuthContext';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// URS-F285: fila draggable
function SortableVisitRow({ visit: v, index: i, lang, lb, isAdmin, studyQuestionnaires, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: v.visit_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <TableRow ref={setNodeRef} style={style} data-testid={`visit-row-${v.code}`} className={isDragging ? 'bg-blue-50' : ''}>
      <TableCell className="w-8 px-1">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-stone-400 hover:text-stone-700 p-1"
          aria-label={lb.drag_hint}
          title={lb.drag_hint}
          data-testid={`drag-handle-${v.code}`}
        >
          <DotsSixVertical size={16} weight="bold" />
        </button>
      </TableCell>
      <TableCell className="font-mono text-xs text-stone-500">{i + 1}</TableCell>
      <TableCell className="font-mono text-sm font-semibold text-stone-900">{v.code}</TableCell>
      <TableCell className="text-sm text-stone-700">{v.name || '-'}</TableCell>
      <TableCell className="text-sm text-stone-600">{lang === 'es' ? `Dia ${v.day}` : `Day ${v.day}`}</TableCell>
      <TableCell className="text-xs text-stone-500">
        {v.window_before || v.window_after ? `${v.window_before ? `-${v.window_before}` : '0'} / +${v.window_after || 0} ${lang === 'es' ? 'dias' : 'days'}` : '-'}
      </TableCell>
      <TableCell className="text-sm text-stone-600">{v.duration_minutes || 15} min</TableCell>
      <TableCell className="text-sm text-stone-600 max-w-[200px] truncate">{v.description || '-'}</TableCell>
      <TableCell>
        {(() => {
          // URS-F289: render multi-asignaciones + presenciales
          const assignments = Array.isArray(v.satisfaction_assignments) && v.satisfaction_assignments.length
            ? v.satisfaction_assignments
            : (v.satisfaction_questionnaire_id
              ? [{ questionnaire_id: v.satisfaction_questionnaire_id, send_email: true }]
              : []);
          const hasContent = (v.questionnaire_ids || []).length > 0 || assignments.length > 0;
          if (!hasContent) return <span className="text-xs text-stone-400">-</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {(v.questionnaire_ids || []).map(qid => {
                const q = studyQuestionnaires.find(sq => sq.questionnaire_id === qid);
                return q ? <Badge key={qid} variant="outline" className="text-[9px] bg-stone-50">{q.name}</Badge> : null;
              })}
              {assignments.map(a => {
                const sq = studyQuestionnaires.find(x => x.questionnaire_id === a.questionnaire_id);
                if (!sq) return null;
                const emailOn = a.send_email !== false;
                return (
                  <Badge
                    key={`sat-${a.questionnaire_id}`}
                    variant="outline"
                    className={`text-[9px] ${emailOn ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-stone-50 text-stone-600 border-stone-300'}`}
                    title={emailOn
                      ? (lang === 'es' ? 'Satisfaccion · envio automatico 2 dias antes' : 'Satisfaction · auto-email 2 days before')
                      : (lang === 'es' ? 'Satisfaccion · solo envio manual' : 'Satisfaction · manual only')}
                  >
                    {emailOn ? '✨' : '📭'} {sq.name}
                  </Badge>
                );
              })}
            </div>
          );
        })()}
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-visit-${v.code}`} onClick={onEdit}><PencilSimple size={14} /></Button>
          {isAdmin && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" data-testid={`delete-visit-${v.code}`} onClick={onDelete}><Trash size={14} /></Button>}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function VisitsPanel({ studyId, lang }) {
  const { user } = useAuth();
  const isAdmin = (user?.roles || (user?.role ? [user.role] : [])).includes('admin');
  const [visits, setVisits] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', day: 0, window_before: 0, window_after: 0, duration_minutes: 15, description: '', order: 0, questionnaire_ids: [], satisfaction_assignments: [], satisfaction_questionnaire_id: null });
  const [saving, setSaving] = useState(false);
  const [confirmDeleteVisit, setConfirmDeleteVisit] = useState(null);
  const [studyQuestionnaires, setStudyQuestionnaires] = useState([]);

  const lb = lang === 'es'
    ? { new: 'Nueva Visita', edit: 'Editar Visita', code: 'Codigo (T0, T7...)', name: 'Nombre', day: 'Dia del estudio', window_before: 'Ventana antes (dias)', window_after: 'Ventana despues (dias)', duration: 'Duracion (min)', description: 'Descripcion / Procedimientos', order: 'Orden', save: 'Guardar', cancel: 'Cancelar', no_visits: 'No hay visitas definidas', confirm_delete: 'Eliminar esta visita?', window: 'Ventana', questionnaires: 'Cuestionarios', select_questionnaires: 'Seleccionar cuestionarios para esta visita', no_questionnaires: 'No hay cuestionarios creados en este estudio', drag_hint: 'Arrastra para reordenar', reorder_saved: 'Orden de visitas actualizado' }
    : { new: 'New Visit', edit: 'Edit Visit', code: 'Code (T0, T7...)', name: 'Name', day: 'Study day', window_before: 'Window before (days)', window_after: 'Window after (days)', duration: 'Duration (min)', description: 'Description / Procedures', order: 'Order', save: 'Save', cancel: 'Cancel', no_visits: 'No visits defined', confirm_delete: 'Delete this visit?', window: 'Window', questionnaires: 'Questionnaires', select_questionnaires: 'Select questionnaires for this visit', no_questionnaires: 'No questionnaires created in this study', drag_hint: 'Drag to reorder', reorder_saved: 'Visit order updated' };

  // URS-F285: drag&drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Lista ya ordenada por `order` (sirve como base estable para el dnd)
  const orderedVisits = [...visits].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
      || (a.day ?? 0) - (b.day ?? 0)
      || (a.code || '').localeCompare(b.code || '')
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = orderedVisits.findIndex(v => v.visit_id === active.id);
    const newIdx = orderedVisits.findIndex(v => v.visit_id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const newList = arrayMove(orderedVisits, oldIdx, newIdx);
    // Optimistic UI: actualizar inmediatamente el order local
    setVisits(newList.map((v, i) => ({ ...v, order: i })));
    try {
      await axios.post(
        `${API_URL}/api/studies/${studyId}/visits/reorder-manual`,
        { visit_ids_in_order: newList.map(v => v.visit_id) },
        { withCredentials: true },
      );
      toast.success(lb.reorder_saved);
    } catch (err) {
      toast.error(errText(err));
      load(); // Revertir desde servidor
    }
  };

  const load = () => {
    if (!studyId) return;
    Promise.all([
      axios.get(`${API_URL}/api/studies/${studyId}/visits`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${studyId}/questionnaires`, { withCredentials: true }),
    ]).then(([vr, qr]) => {
      setVisits(vr.data);
      setStudyQuestionnaires(qr.data);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, [studyId]);

  const openCreate = () => {
    setEditing(null);
    const nextDay = visits.length > 0 ? Math.max(...visits.map(v => v.day)) + 7 : 0;
    setForm({ code: `T${nextDay}`, name: '', day: nextDay, window_before: 0, window_after: 0, duration_minutes: 15, description: '', order: visits.length, questionnaire_ids: [], satisfaction_assignments: [], satisfaction_questionnaire_id: null });
    setShowDialog(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    // URS-F289: cargar satisfaction_assignments. Compat: si no existe en BD,
    // intentar reconstruir desde el legacy single field.
    const assignments = Array.isArray(v.satisfaction_assignments) && v.satisfaction_assignments.length
      ? v.satisfaction_assignments
      : (v.satisfaction_questionnaire_id
        ? [{ questionnaire_id: v.satisfaction_questionnaire_id, send_email: true, added_at: new Date().toISOString() }]
        : []);
    setForm({ code: v.code, name: v.name, day: v.day, window_before: v.window_before, window_after: v.window_after, duration_minutes: v.duration_minutes || 15, description: v.description || '', order: v.order, questionnaire_ids: v.questionnaire_ids || [], satisfaction_assignments: assignments, satisfaction_questionnaire_id: v.satisfaction_questionnaire_id || null });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error(lang === 'es' ? 'El codigo es obligatorio' : 'Code is required'); return; }
    setSaving(true);
    // URS-F289 fix: el legacy `satisfaction_questionnaire_id` siempre debe
    // derivarse del array `satisfaction_assignments`. Si NO derivamos, al
    // deseleccionar el ultimo checkbox el backend ve el legacy field con el
    // valor viejo y lo reincorpora a assignments (sanitize) -> el cuestionario
    // "no se deja deseleccionar".
    const payload = {
      ...form,
      satisfaction_questionnaire_id: form.satisfaction_assignments?.length
        ? form.satisfaction_assignments[0].questionnaire_id
        : null,
    };
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/studies/${studyId}/visits/${editing.visit_id}`, payload, { withCredentials: true });
        toast.success(lang === 'es' ? 'Visita actualizada' : 'Visit updated');
      } else {
        await axios.post(`${API_URL}/api/studies/${studyId}/visits`, payload, { withCredentials: true });
        toast.success(lang === 'es' ? 'Visita creada' : 'Visit created');
      }
      setShowDialog(false);
      load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const handleDelete = async (visitId) => {
    try {
      await axios.delete(`${API_URL}/api/studies/${studyId}/visits/${visitId}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Visita eliminada' : 'Visit deleted');
      setConfirmDeleteVisit(null);
      load();
    } catch (err) { toast.error(errText(err)); }
  };

  if (!studyId) return <p className="text-sm text-stone-500 text-center py-8">{lang === 'es' ? 'Guarda el estudio primero para gestionar visitas' : 'Save the study first to manage visits'}</p>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-xs text-stone-500 italic">{visits.length > 0 ? lb.drag_hint : ''}</p>
        <Button data-testid="create-visit-btn" onClick={openCreate} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
          <Plus size={16} />{lb.new}
        </Button>
      </div>

      {visits.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <p className="text-sm text-stone-500">{lb.no_visits}</p>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead className="w-12">#</TableHead>
              <TableHead className="w-24">{lb.code}</TableHead>
              <TableHead>{lb.name}</TableHead>
              <TableHead className="w-24">{lb.day}</TableHead>
              <TableHead className="w-32">{lb.window}</TableHead>
              <TableHead className="w-20">{lb.duration}</TableHead>
              <TableHead>{lb.description}</TableHead>
              <TableHead className="w-28">{lb.questionnaires}</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow></TableHeader>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={orderedVisits.map(v => v.visit_id)} strategy={verticalListSortingStrategy}>
                <TableBody>
                  {orderedVisits.map((v, i) => (
                    <SortableVisitRow
                      key={v.visit_id}
                      visit={v}
                      index={i}
                      lang={lang}
                      lb={lb}
                      isAdmin={isAdmin}
                      studyQuestionnaires={studyQuestionnaires}
                      onEdit={() => openEdit(v)}
                      onDelete={() => setConfirmDeleteVisit(v)}
                    />
                  ))}
                </TableBody>
              </SortableContext>
            </DndContext>
          </Table>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader><DialogTitle>{editing ? lb.edit : lb.new}</DialogTitle></DialogHeader>
          <div className="space-y-4 flex-1 overflow-y-auto pr-2 -mr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{lb.code} <span className="text-red-500">*</span></Label>
                <Input data-testid="visit-code-input" value={form.code} onChange={e => {
                  const code = e.target.value.toUpperCase();
                  // URS-F436: si el codigo tiene formato T{N}D? (T0, T14D, T28D...) y
                  // el campo "Dia" sigue en 0, se auto-rellena con el numero implicito.
                  // NO se pisa si el usuario ya lo edito manualmente (day > 0).
                  const m = code.match(/^(?:.+\+)?T(\d+)D?$/);
                  const derivedDay = m ? parseInt(m[1], 10) : null;
                  setForm(p => ({
                    ...p,
                    code,
                    day: (derivedDay !== null && (p.day === 0 || p.day === '' || p.day == null)) ? derivedDay : p.day,
                  }));
                }} placeholder="T0" className="font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label>{lb.day}</Label>
                <Input data-testid="visit-day-input" type="number" value={form.day} onChange={e => setForm(p => ({ ...p, day: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{lb.name}</Label>
              <Input data-testid="visit-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder={lang === 'es' ? 'Screening, Basal, Seguimiento...' : 'Screening, Baseline, Follow-up...'} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{lb.window_before}</Label>
                <Input data-testid="visit-window-before" type="number" min="0" value={form.window_before} onChange={e => setForm(p => ({ ...p, window_before: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="space-y-1.5">
                <Label>{lb.window_after}</Label>
                <Input data-testid="visit-window-after" type="number" min="0" value={form.window_after} onChange={e => setForm(p => ({ ...p, window_after: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{lb.duration}</Label>
              <Input data-testid="visit-duration-input" type="number" min="5" step="5" value={form.duration_minutes} onChange={e => setForm(p => ({ ...p, duration_minutes: parseInt(e.target.value) || 15 }))} />
            </div>
            <div className="space-y-1.5">
              <Label>{lb.description}</Label>
              <Textarea data-testid="visit-description-input" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="min-h-[60px]" />
            </div>
            <div className="space-y-1.5">
              <Label>{lb.order}</Label>
              <Input data-testid="visit-order-input" type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} />
            </div>
            <div className="space-y-1.5">
              <Label>{lb.questionnaires}</Label>
              {studyQuestionnaires.length === 0 ? (
                <p className="text-xs text-stone-400">{lb.no_questionnaires}</p>
              ) : (
                <div className="max-h-[150px] overflow-y-auto space-y-1 border border-stone-200 rounded-sm p-2 bg-stone-50">
                  {/* URS-F278: los cuestionarios de tipo 'satisfaccion' NO se asignan
                      como presenciales; van por el selector de Satisfaccion (email) abajo. */}
                  {studyQuestionnaires.filter(q => q.type !== 'inclusion_exclusion' && q.type !== 'satisfaccion').map(q => (
                    <label key={q.questionnaire_id} className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border cursor-pointer transition-colors ${form.questionnaire_ids.includes(q.questionnaire_id) ? 'bg-[#1f3e80]/5 border-[#1f3e80]/30' : 'bg-white border-stone-200'}`}>
                      <input type="checkbox" checked={form.questionnaire_ids.includes(q.questionnaire_id)}
                        onChange={() => setForm(p => ({ ...p, questionnaire_ids: p.questionnaire_ids.includes(q.questionnaire_id) ? p.questionnaire_ids.filter(id => id !== q.questionnaire_id) : [...p.questionnaire_ids, q.questionnaire_id] }))}
                        className="rounded border-stone-300 text-[#1f3e80] focus:ring-[#1f3e80]" />
                      <span className="text-sm text-stone-700">{q.name}</span>
                      <Badge variant="outline" className="text-[9px] ml-auto">{(q.questions || []).length} preg.</Badge>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {/* URS-F289: multi-asignacion + toggle email por cuestionario */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-2">
                {lang === 'es' ? 'Cuestionarios de Satisfaccion (envio 2 dias antes)' : 'Satisfaction Questionnaires (sent 2 days before)'}
                <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">Email</Badge>
              </Label>
              {studyQuestionnaires.filter(q => q.type === 'satisfaccion').length === 0 ? (
                <p className="text-xs text-stone-400">{lang === 'es' ? 'Crea primero un cuestionario tipo "Satisfaccion" en la pestana Cuestionarios.' : 'Create a "Satisfaction" questionnaire first in the Questionnaires tab.'}</p>
              ) : (
                <div className="border border-stone-200 rounded-sm p-2 bg-stone-50 space-y-1">
                  <p className="text-[11px] text-stone-500 px-1">{lang === 'es' ? 'Marca uno o varios cuestionarios. Por defecto se envian automaticamente por email; desactiva el toggle para enviarlos solo manualmente.' : 'Check one or more questionnaires. By default they are auto-emailed; toggle off to send manually only.'}</p>
                  {studyQuestionnaires.filter(q => q.type === 'satisfaccion').map(q => {
                    const assignment = (form.satisfaction_assignments || []).find(a => a.questionnaire_id === q.questionnaire_id);
                    const checked = !!assignment;
                    const sendEmail = assignment?.send_email !== false; // default true
                    return (
                      <div key={q.questionnaire_id} className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border ${checked ? 'bg-violet-50 border-violet-300' : 'bg-white border-stone-200'}`}>
                        <label className="flex items-center gap-2 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setForm(p => {
                                const list = [...(p.satisfaction_assignments || [])];
                                if (checked) {
                                  return { ...p, satisfaction_assignments: list.filter(a => a.questionnaire_id !== q.questionnaire_id) };
                                }
                                list.push({ questionnaire_id: q.questionnaire_id, send_email: true, added_at: new Date().toISOString() });
                                return { ...p, satisfaction_assignments: list };
                              });
                            }}
                            data-testid={`satq-check-${q.questionnaire_id}`}
                            className="accent-violet-600"
                          />
                          <span className="text-sm text-stone-700">{q.name}</span>
                          <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-600 border-violet-200">{(q.questions || []).length} preg.</Badge>
                        </label>
                        {checked && (
                          <label className="flex items-center gap-1.5 text-[11px] text-stone-600 cursor-pointer pl-2 border-l border-violet-200" title={lang === 'es' ? 'Envio automatico por email 2 dias antes' : 'Auto-email 2 days before'}>
                            <input
                              type="checkbox"
                              checked={sendEmail}
                              onChange={(e) => {
                                const next = e.target.checked;
                                setForm(p => ({
                                  ...p,
                                  satisfaction_assignments: (p.satisfaction_assignments || []).map(a =>
                                    a.questionnaire_id === q.questionnaire_id ? { ...a, send_email: next } : a
                                  ),
                                }));
                              }}
                              data-testid={`satq-email-${q.questionnaire_id}`}
                              className="accent-violet-600"
                            />
                            <span>{sendEmail ? (lang === 'es' ? '📧 Email ON' : '📧 Email ON') : (lang === 'es' ? '📭 Solo manual' : '📭 Manual only')}</span>
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>{lb.cancel}</Button>
            <Button data-testid="save-visit-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : lb.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog
        open={!!confirmDeleteVisit}
        onOpenChange={(v) => { if (!v) setConfirmDeleteVisit(null); }}
        title={lang === 'es' ? 'Eliminar visita' : 'Delete visit'}
        description={lang === 'es' ? `¿Estás seguro de que deseas eliminar la visita "${confirmDeleteVisit?.code || ''}"?` : `Are you sure you want to delete visit "${confirmDeleteVisit?.code || ''}"?`}
        onConfirm={() => handleDelete(confirmDeleteVisit?.visit_id)}
      />
    </div>
  );
}
